import type { AdminVehicleGenerationDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { marketName, restylingName } from '../_lib/generationLabel';

/**
 * One generation of the model dialog's list (AUT-450 / VG-03), after
 * `vehicle-generations.html`: the display string, «N поколение · рестайлинг»,
 * chips (market, «скрыто», source) and Изменить / Скрыть|Показать. A hidden
 * row is muted.
 */
export interface GenerationRowProps {
  generation: AdminVehicleGenerationDto;
  disabled: boolean;
  onEdit: (g: AdminVehicleGenerationDto) => void;
  onToggleStatus: (g: AdminVehicleGenerationDto) => void;
}

const K = 'vehicleCatalog.generations';

const RowChips = ({ g }: { g: AdminVehicleGenerationDto }): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
      {g.market !== '' ? (
        <Chip size="small" color="info" variant="outlined" label={marketName(t, g.market)} />
      ) : null}
      {g.status === 'hidden' ? <Chip size="small" label={t(`${K}.hiddenChip`)} /> : null}
      <Chip
        size="small"
        variant="outlined"
        color={g.source === 'staff' ? 'success' : 'default'}
        label={t(`${K}.source.${g.source}`)}
      />
    </Box>
  );
};

export const GenerationRow = ({
  generation: g,
  disabled,
  onEdit,
  onToggleStatus,
}: GenerationRowProps): ReactElement => {
  const { t } = useTranslation();
  const hidden = g.status === 'hidden';
  return (
    <Box
      component="li"
      data-status={g.status}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2,
        py: 1,
        borderBottom: 1,
        borderColor: 'divider',
        opacity: hidden ? 0.6 : 1,
        '&:last-of-type': { borderBottom: 0 },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600}>
          {g.display}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t(`${K}.meta`, { n: g.generation, restyling: restylingName(t, g.restyling) })}
        </Typography>
      </Box>
      <RowChips g={g} />
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Button
          size="small"
          disabled={disabled}
          aria-label={t(`${K}.editAria`, { display: g.display })}
          onClick={() => onEdit(g)}
        >
          {t(`${K}.edit`)}
        </Button>
        <Button
          size="small"
          color={hidden ? 'primary' : 'inherit'}
          disabled={disabled}
          aria-label={t(hidden ? `${K}.showAria` : `${K}.hideAria`, { display: g.display })}
          onClick={() => onToggleStatus(g)}
        >
          {t(hidden ? `${K}.show` : `${K}.hide`)}
        </Button>
      </Box>
    </Box>
  );
};
